import {computed, DestroyRef, inject, Injectable, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {catchError, of} from 'rxjs';
import {BookingReferenceService} from '../../location/booking-entity-overview/booking-reference.service';
import {TableLayoutHttpService} from '../../table-layout/data-access/table-layout-http.service';
import {DeviceConfigHttpService} from '../../device-config/data-access/device-config-http.service';
import {
  FloorArea,
  FloorPlanBackground,
  FloorTable
} from '../../table-layout/shared/models/table-layout.model';
import {
  backgroundHeight,
  backgroundWidthOrNatural
} from '../../table-layout/shared/models/floor-plan-geometry';
import {RenderedBackground} from '../../table-layout/manager/floor-plan-canvas.component';
import {
  BookingReferenceHelpers,
  IBookingReference
} from '../../../_contracts/Booking/BookingReferences/booking-reference';
import {
  BookingReferenceEntityState
} from '../../../_models/_booking/_enums/booking-reference-entity-state';
import {CompositionDataHelpers} from '../../../_contracts/Booking/Articles/composition-data';
import {
  agoMin,
  DEFAULT_IDLE_WARNING_LEVELS,
  FREE_TABLE_APPEARANCE,
  IdleWarningLevelConfig,
  readIdleWarningLevels,
  readIdleWarningsEnabled,
  resolveIdleLevel
} from '../shared/idle-warning-levels';
import {FreeTable, OpenTable, OpenTableItem, OpenTablePlacement} from '../shared/open-tables.model';

/**
 * How often the clock is bumped so timers keep ageing **between** pushes, in milliseconds.
 *
 * The reference implementation used exactly this interval as a stand-in for a live feed; here it is the opposite —
 * the feed is real and this is only what keeps a table that has had *no* new booking counting up. A table's level
 * has to escalate on its own, or the screen would go quiet precisely when a guest is being forgotten (the whole
 * point of it). 10 s is a comfortable factor below the finest thing displayed (one minute).
 */
export const TICK_MS = 10_000;

/** Everything the screen needs, merged from three independent sources. */
@Injectable()
export class OpenTablesStore {
  private readonly references$ = inject(BookingReferenceService);
  private readonly layout = inject(TableLayoutHttpService);
  private readonly deviceConfig = inject(DeviceConfigHttpService);
  private readonly destroyRef = inject(DestroyRef);

  // region Raw sources

  /** Live booking references, pushed by `BookingReferenceService.EntitiesChanged` (shard-scoped SignalR). */
  private readonly references = signal<readonly IBookingReference[]>([]);

  /** Floor-plan geometry. NOT pushed live — see {@link refreshGeometry}. */
  private readonly tables = signal<readonly FloorTable[]>([]);
  private readonly areas = signal<readonly FloorArea[]>([]);
  private readonly backgroundMeta = signal<FloorPlanBackground | null>(null);
  private readonly backgroundObjectUrl = signal<string | null>(null);
  private readonly backgroundNaturalSize = signal<{width: number; height: number} | null>(null);

  /** The shard-effective device configuration's escalation levels + master switch. */
  private readonly levels = signal<readonly IdleWarningLevelConfig[]>(DEFAULT_IDLE_WARNING_LEVELS);
  private readonly warningsEnabled = signal(true);

  /** Wall clock, bumped every {@link TICK_MS} so idle durations keep growing without a push. */
  private readonly now = signal(Date.now());

  /** `true` while the first reference payload has not arrived yet (the screen shows a skeleton/empty state). */
  readonly loading = signal(true);

  /** Set when the live feed reported a failure, so the screen can say so instead of showing an empty floor. */
  readonly feedFailed = signal(false);

  // endregion

  constructor() {
    this.subscribeToReferences();
    this.refreshGeometry();
    this.refreshConfiguration();

    const tick = setInterval(() => this.now.set(Date.now()), TICK_MS);
    this.destroyRef.onDestroy(() => {
      clearInterval(tick);
      this.releaseObjectUrl();
    });
  }

  // region Derived state

  /** The configured escalation levels, ascending — what the map legend renders. */
  readonly warningLevels = computed(() => this.warningsEnabled() ? this.levels() : []);

  /** The floor-plan areas, for the map's zones. */
  readonly floorAreas = computed(() => this.areas());

  /** The background image resolved to render size, or `null`. */
  readonly background = computed<RenderedBackground | null>(() => {
    const meta = this.backgroundMeta();
    const url = this.backgroundObjectUrl();
    const natural = this.backgroundNaturalSize();
    if (!meta || !url || !natural) return null;
    // The height is never stored — it is always derived from the width and the decoded aspect ratio, which is what
    // makes the placement impossible to distort (see `floor-plan-geometry.backgroundHeight`).
    const width = backgroundWidthOrNatural(meta.width, natural.width);
    return {
      objectUrl: url, x: meta.x, y: meta.y, width,
      height: backgroundHeight(width, natural.width, natural.height), opacity: meta.opacity
    };
  });

  /**
   * The open tables — one row per live reference, joined onto its floor-plan table.
   *
   * **The join is `reference.entityId === table.id`.** A booking reference names the *entity* it is booked against;
   * for `BookingReferenceType.Table` that entity id IS the persistent table's id. A reference that finds no table
   * is kept rather than dropped: it has real money on it and a service needs to see it — it simply has no
   * geometry, so it is list-only.
   *
   * **The join no longer decides what a row IS.** It used to: `adHoc: table === null` turned "I could not find
   * this table" into the claim "this is a walk-in", which is a fact only the server holds. A booking that had
   * attached itself to a **soft-deleted** table therefore reached the floor badged `ad hoc` — the reported bug.
   * The placement is now read from `reference.entityState`, which the monolith states outright, and the join is
   * left to answer only the question it can: is there geometry to draw?
   *
   * **Reconciling the two.** They can disagree, and a disagreement is never resolved in the client's favour:
   *  * `Placed` **with** no matching table — stale geometry, or a race with an admin editing the plan in another
   *    tab (geometry is fetched, not pushed; see {@link refreshGeometry}). The row renders **list-only**, because
   *    there is genuinely no position to draw it at, but it keeps its stated `placed` label: no `ad hoc` badge, no
   *    `entfernt` badge. Inventing "walk-in" from a missing row is exactly the guess that caused the bug, and the
   *    header's refresh re-reads the plan, at which point the marker appears by itself.
   *  * `Unplaced`/`Removed` **with** a matching table — a table id reused by a live row. The stated state still
   *    wins for the label; the geometry is still used for the marker, seats and area, because it is real.
   */
  readonly openTables = computed<OpenTable[]>(() => {
    const tablesById = new Map(this.tables().map(table => [table.id, table]));
    const areasById = new Map(this.areas().map(area => [area.id, area]));
    const now = this.now();
    const levels = this.levels();
    const enabled = this.warningsEnabled();

    return this.references().map(reference => {
      const table = tablesById.get(reference.entityId) ?? null;
      const items = toItems(reference);
      const booked = reference.compositionData ?? [];
      const lastActivity = parseTimestamp(reference.lastActivityUtc);
      const idleMinutes = agoMin(now, lastActivity);
      return {
        id: reference.id,
        table,
        placement: toPlacement(reference.entityState),
        number: table?.name || reference.entityName || '—',
        areaName: table?.areaId ? areasById.get(table.areaId)?.name ?? null : null,
        seats: table && table.seats > 0 ? table.seats : null,
        items,
        // Every line that was on this reference has been paid off. The row is still OPEN (the reference has
        // not been closed), so it belongs on this screen — but its item block would otherwise be blank.
        settledOnly: booked.length > 0 && items.length === 0,
        // Settled lines net to a quantity of 0 and therefore to a line total of 0, so summing the *filtered*
        // list gives exactly the same open amount as summing them all — the header's aggregate is unchanged.
        total: items.reduce((sum, item) => sum + item.total, 0),
        lastActivity,
        idleMinutes,
        level: resolveIdleLevel(idleMinutes, levels, enabled),
        // Same UTC normalization as `lastActivityUtc` — the monolith omits the trailing `Z` on some paths, and an
        // opening time read as local would be an hour or two out in a German summer.
        openedUtc: parseTimestamp(reference.openedUtc),
        // `null`/`undefined`/`0` all mean "no limit" on the wire; collapse them here so no component repeats it.
        spendingLimit: BookingReferenceHelpers.hasSpendingLimit(reference) ? reference.spendingLimit! : null,
        // The shared helper, never a local `limit - total`: it is the one definition of the headroom, and it
        // agrees with `total` above by construction (a settled line nets to a quantity of 0, hence to 0 €).
        headroom: BookingReferenceHelpers.getHeadroom(reference)
      };
    });
  });

  /**
   * Only the rows that carry geometry — the ones the map can actually draw.
   *
   * The test is on the **join**, not on the placement: geometry is the only thing that yields a position, so a
   * `placed` row the plan does not (yet) contain stays off the map just the same. It is a rendering question, and
   * the answer must not change just because the server now labels the row differently.
   */
  readonly mappedTables = computed(() => this.openTables().filter(row => row.table !== null));

  /** Every persistent table with no open reference on it — the optional muted markers. */
  readonly freeTables = computed<FreeTable[]>(() => {
    const occupied = new Set(this.openTables().map(row => row.table?.id).filter((id): id is string => !!id));
    return this.tables()
      .filter(table => !occupied.has(table.id))
      .map(table => ({table, number: table.name}));
  });

  /** How the free markers are painted — a single shared appearance, exported so the map need not know the colours. */
  readonly freeAppearance = FREE_TABLE_APPEARANCE;

  /** Sum of every open table's total, in euros — the header's "… € offen". */
  readonly openTotal = computed(() => this.openTables().reduce((sum, row) => sum + row.total, 0));

  /** How many open tables have escalated past "fresh" — the header's Aufmerksamkeit pill. */
  readonly attentionCount = computed(() => this.openTables().filter(row => !row.level.fresh).length);

  /** The ticking clock, exposed so the header can render it and specs can assert the ageing. */
  readonly clock = computed(() => this.now());

  // endregion

  // region Commands

  /**
   * Re-fetches the floor plan.
   *
   * Geometry is the one input that is **not** pushed: an admin editing the layout in another tab does not notify
   * this screen. It is refetched on demand (the header's refresh) and on SignalR reconnect, where the gap since the
   * last known-good state is unbounded.
   */
  refreshGeometry(): void {
    this.layout.listTables()
      .pipe(catchError(() => of([] as FloorTable[])), takeUntilDestroyed(this.destroyRef))
      .subscribe(tables => this.tables.set(tables));

    this.layout.listAreas()
      .pipe(catchError(() => of([] as FloorArea[])), takeUntilDestroyed(this.destroyRef))
      .subscribe(areas => this.areas.set(areas));

    this.layout.getBackground()
      .pipe(catchError(() => of(null)), takeUntilDestroyed(this.destroyRef))
      .subscribe(background => {
        this.backgroundMeta.set(background);
        if (background) this.loadBackgroundImage();
        else this.releaseObjectUrl();
      });
  }

  /**
   * Re-reads the shard-effective device configuration (company ← shard, no device layer).
   *
   * A failure is silent by design: the screen still works on the catalog defaults, and an operator staring at a
   * floor does not need a toast about a configuration endpoint.
   */
  refreshConfiguration(): void {
    this.deviceConfig.effectiveForShard()
      .pipe(catchError(() => of(null)), takeUntilDestroyed(this.destroyRef))
      .subscribe(configuration => {
        this.levels.set(readIdleWarningLevels(configuration?.settings));
        this.warningsEnabled.set(readIdleWarningsEnabled(configuration?.settings));
      });
  }

  /** Forces a re-age without waiting for the tick — used right after a push so the new row is not a tick stale. */
  touchClock(): void {
    this.now.set(Date.now());
  }

  /**
   * Writes a just-saved spending limit onto the held reference, **optimistically** (#204).
   *
   * The screen is live, so the value would arrive on its own — but only when the next SignalR push happens, which
   * for a quiet table can be minutes away. Until then the card would keep showing the number the admin just
   * replaced, which reads as a failed save. The server's own answer is authoritative and simply overwrites this
   * on the next push.
   *
   * Applied by **replacing** the reference rather than mutating it in place: the rows are a `computed` over the
   * `references` signal, and a mutation of an object the signal already holds is not a change the signal can see.
   * (The retired screen mutated its reference directly and got away with it only because it re-rendered on
   * Angular's default change detection.)
   *
   * @param referenceId The booking reference's id — the same identity the cards are keyed on.
   * @param limit The new limit in euros, or `null` to clear it.
   */
  applySpendingLimit(referenceId: string, limit: number | null): void {
    this.references.update(references => references.map(reference =>
      reference.id === referenceId ? {...reference, spendingLimit: limit} : reference));
  }

  // endregion

  private subscribeToReferences(): void {
    this.references$.EntitiesChanged
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(references => {
        this.loading.set(false);
        if (references === undefined) {
          // `DtoBaseService.LoadData` sets `Entities` to undefined on a failed fetch. Keep whatever is on screen —
          // a stale floor plan is far more useful to a service than a blank one — but say so.
          this.feedFailed.set(true);
          return;
        }
        this.feedFailed.set(false);
        this.references.set(references);
        // The ageing is derived from `now`; refresh it with the push so a brand-new booking reads "0 Min"
        // immediately rather than up to one tick late.
        this.touchClock();
      });
  }

  private loadBackgroundImage(): void {
    // `/booking/FloorPlan/Image` is authenticated, so the bytes must ride the HTTP client and become an object URL;
    // pointing CSS `url()` at the route fires an unauthenticated request that 401s.
    this.layout.downloadBackgroundImage()
      .pipe(catchError(() => of(null)), takeUntilDestroyed(this.destroyRef))
      .subscribe(blob => {
        if (!blob) {
          this.releaseObjectUrl();
          return;
        }
        const url = URL.createObjectURL(blob);
        this.releaseObjectUrl();
        this.backgroundObjectUrl.set(url);

        // Decode to learn the aspect ratio — the one thing the backend deliberately does not store.
        const image = new Image();
        image.onload = () => {
          const width = image.naturalWidth || 1000;
          this.backgroundNaturalSize.set({width, height: image.naturalHeight || width});
        };
        image.src = url;
      });
  }

  private releaseObjectUrl(): void {
    const url = this.backgroundObjectUrl();
    if (url) URL.revokeObjectURL(url);
    this.backgroundObjectUrl.set(null);
    this.backgroundNaturalSize.set(null);
  }
}

/**
 * Reads the wire's stated entity state into the screen's {@link OpenTablePlacement}.
 *
 * A **missing** value reads as `placed`, deliberately: the field is optional on the wire (the monolith serializes
 * with `NullValueHandling.Ignore`, and a payload minted before #205 simply has no such property), and `placed` is
 * the state that adds no badge and makes no claim. An unknown future value falls into the same default rather than
 * crashing the screen — the enum is append-only, so a newer server may well send one.
 */
function toPlacement(state: BookingReferenceEntityState | undefined): OpenTablePlacement {
  switch (state) {
    case BookingReferenceEntityState.Unplaced:
      return 'unplaced';
    case BookingReferenceEntityState.Removed:
      return 'removed';
    default:
      return 'placed';
  }
}

/**
 * Flattens a reference's composition data into the lines that are still **open**.
 *
 * **Settled lines are dropped.** A reference's `quantity` is the netted open balance (booked minus paid — see
 * {@link CompositionDataHelpers.hasOpenBalance}), so a fully paid line arrives as `0×` and reads `0,00 €`. It is
 * noise on a screen whose whole job is "what is still owed at this table", and the MAUI register hides it from
 * its own table view for the same reason. The predicate is the shared helper, never a local `price === 0` test:
 * a free item is not a paid one.
 *
 * Money stays in **euros**: `singlePrice` is already a decimal amount and the line total goes through the shared
 * {@link CompositionDataHelpers.getTotalPrice} rather than being multiplied here — the central-calculator rule.
 *
 * The interchanged-component lines are passed straight through (#423). They are **not** derived here: the server
 * renders them once, through the one formatter every surface shares, because building them needs the
 * composition's slot graph (for ordering and for untouched-but-interchangeable defaults) that no client holds.
 * The `?? []` is the wire guard — the field is optional on {@link ICompositionData} so a pre-#423 monolith still
 * type-checks, while {@link OpenTableItem.modificationLines} is unconditionally an array from here on.
 */
function toItems(reference: IBookingReference): OpenTableItem[] {
  return (reference.compositionData ?? [])
    .filter(data => CompositionDataHelpers.hasOpenBalance(data))
    .map(data => ({
      qty: `${data.quantity}×`,
      quantity: data.quantity,
      name: data.composition?.name ?? '',
      total: CompositionDataHelpers.getTotalPrice(data),
      modificationLines: data.modificationLines ?? []
    }));
}

/**
 * Reads a wire timestamp (`lastActivityUtc`, `openedUtc`) into epoch milliseconds.
 *
 * Two shapes reach this function, and both must land on the same instant:
 *  * **A `Date`** — the normal case for anything fetched over `HttpClient`. The app-wide `DateInterceptor` has already
 *    converted every ISO timestamp in the response body (appending the `Z` a zone-less value lacks), so the instant
 *    is final and is taken as-is. It must never be stringified and re-parsed: `${date}` is the browser-local
 *    `"Wed Sep 16 2026 23:42:13 GMT+0200 (…)"`, and V8 reads that string with a `Z` appended as the *Berlin wall
 *    clock in UTC* — the constant +2 h of Bug #677 — and drops the milliseconds on top.
 *  * **A string** — fixtures, stories, and any payload that did not pass the interceptor. The monolith serializes
 *    DateTimes as UTC but **without** a trailing `Z` in some paths, which `Date.parse` would read as *local* time, so
 *    a bare `yyyy-MM-ddTHH:mm:ss[.fff]` is explicitly marked as UTC before parsing.
 */
export function parseTimestamp(value: string | Date | null | undefined): number | null {
  if (!value) return null;
  if (value instanceof Date) {
    const epoch = value.getTime();
    return Number.isFinite(epoch) ? epoch : null;
  }
  const normalized = /(Z|[+-]\d{2}:?\d{2})$/.test(value) ? value : `${value}Z`;
  const parsed = Date.parse(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}
